/**
 * 용량 룰 검수 목록 테이블 (순수 프리젠테이션 오거니즘).
 *
 * 비즈니스 로직 없음 — items를 props로 받고 행 클릭을 위로 올린다(ARCHITECTURE §2).
 * 서버 페이징·필터·집계는 상위 Page가 담당한다.
 */
import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Spinner from "components/atoms/Spinner";
import Button from "components/atoms/Button";
import { AdminDoseRuleListItem } from "services/api/doseRules/types";
import {
  VERSION_SECTION_TITLE,
  doseRuleStatusLabel,
  formatDateTime,
  formatVersionNumber,
} from "utils/doseRule";

export interface DoseRuleListSectionProps {
  items: AdminDoseRuleListItem[];
  isLoading: boolean;
  isError: boolean;
  onRowClick: (item: AdminDoseRuleListItem) => void;
  onRetry?: () => void;
  /** 필터별 빈 문구(예: 차단 필터면 "차단된 룰이 없습니다"). 기본은 공통 문구. */
  emptyText?: string;
}

const TableWrap = styled.div`
  overflow-x: auto;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  min-width: 960px;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 14px;
  white-space: nowrap;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[10]};
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  font-weight: 600;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
`;

const Td = styled.td`
  padding: 12px 14px;
  vertical-align: top;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[15]};
`;

const Row = styled.tr`
  cursor: pointer;
  &:hover td {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[10]};
  }
`;

const StateCell = styled.td`
  padding: 48px 14px;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
`;

const Muted = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
`;

const CodeLine = styled.div`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

type Tone = "green" | "red" | "orange" | "gray" | "blue";
const TONE: Record<Tone, { bg?: string; fg?: string }> = {
  green: { bg: COLOR_SYSTEM.get("Green")?.[50], fg: COLOR_SYSTEM.get("Green")?.[800] },
  red: { bg: COLOR_SYSTEM.get("Red")?.[50], fg: COLOR_SYSTEM.get("Red")?.[800] },
  orange: { bg: COLOR_SYSTEM.get("Orange")?.[50], fg: COLOR_SYSTEM.get("Orange")?.[800] },
  gray: { bg: COLOR_SYSTEM.get("Gray")?.[50], fg: COLOR_SYSTEM.get("Gray")?.[600] },
  blue: { bg: COLOR_SYSTEM.get("Blue")?.[50], fg: COLOR_SYSTEM.get("Blue")?.[800] },
};

const Badge = styled.span<{ $tone: Tone }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  background-color: ${({ $tone }) => TONE[$tone].bg ?? "#eee"};
  color: ${({ $tone }) => TONE[$tone].fg ?? "#333"};
`;

/**
 * 버전 배지 — 공용 `Badge`보다 **작고 납작하다**(보정 7g).
 *
 * 공용 Badge(12px · padding 2px 8px)는 인라인 박스 높이가 품목명 줄 상자(13px 텍스트)보다
 * 커서 **행 높이를 4px 늘린다**(1280px 실측: 54px → 58px). 상태·경고·구분 배지는 자기 칸을
 * 혼자 쓰니 문제가 없지만, 이 배지는 품목명과 **같은 줄**에 얹히므로 목록 전체 행이 두꺼워진다
 * (AC "품목명 63자 + `vN` 배지가 목록 행 높이를 늘리지 않는다").
 *
 * 그래서 이 배지만 줄 상자 안에 들어가게 줄인다 — 공용 `Badge`는 건드리지 않는다.
 */
const VersionBadge = styled(Badge)`
  font-size: 11px;
  line-height: 1.2;
  padding: 0 6px;
  vertical-align: middle;
`;

/**
 * 시각적으로만 숨긴다 — **`display:none`·`visibility:hidden`을 쓰지 않는다**(둘 다 보조기술에서도
 * 사라진다). `clip` 관용구는 이 repo에 선례가 있다(`molecules/DrugLabelInfo/index.tsx:437`).
 *
 * 왜 필요한가(보정 7b) — 배지 글자는 설계 결정 1대로 `vN`이어야 하는데, 그것만으로는 화면에
 * 이미 노출된 다른 "버전"(`criteria_version`·`tool_version`)과 구분되지 않는다. 비대화형
 * `<span>`에 `aria-label`만 다는 것은 ARIA 1.2가 금지하고(이름을 가질 수 없는 role) 스크린리더가
 * 흔히 무시한다 — **내용으로** 이름을 만든다.
 */
const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const statusTone = (status: string): Tone => {
  if (status === "active") return "green";
  if (status === "pending_review") return "orange";
  if (status === "gate_blocked") return "red"; // 차단 배지는 빨강(D15)
  return "gray";
};

const COLUMN_COUNT = 7;

/**
 * 버전 배지를 달지 판정한다 — **`version > 1`일 때만**.
 *
 * 목록 응답에 `versionCount`는 없지만 은퇴 행이 보존되므로 **현재 버전 번호 N = 총 버전 수 N**
 * 이라 추가 호출 없이 이 값만으로 만든다. `version`은 optional이고 `undefined > 1`은 false라
 * 필드가 없는 백엔드에서도 배지가 저절로 빠진다
 * (spec `2026-09-25-처방검토-관리자룰화면-룰버전-UI` §3-2 4항).
 *
 * **컬럼을 늘리지 않는다** — 배지는 품목 셀 안에 들어간다.
 */
const hasVersionBadge = (version?: number): boolean =>
  typeof version === "number" && version > 1;

const DoseRuleListSection: React.FC<DoseRuleListSectionProps> = ({
  items,
  isLoading,
  isError,
  onRowClick,
  onRetry,
  emptyText = "조건에 맞는 룰이 없습니다.",
}) => {
  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <Th>상태</Th>
            <Th>경고</Th>
            <Th>구분</Th>
            <Th>품목</Th>
            <Th>유형</Th>
            <Th>검수자</Th>
            <Th>수정일</Th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <StateCell colSpan={COLUMN_COUNT}>
                <Spinner />
              </StateCell>
            </tr>
          ) : isError ? (
            <tr>
              <StateCell colSpan={COLUMN_COUNT}>
                <div style={{ marginBottom: 8 }}>목록을 불러오지 못했습니다.</div>
                {onRetry ? (
                  <Button
                    buttonType="button"
                    type="Text"
                    size="Small"
                    variant="Outlined"
                    color="Secondary"
                    onClick={onRetry}
                  >
                    다시 시도
                  </Button>
                ) : null}
              </StateCell>
            </tr>
          ) : items.length === 0 ? (
            <tr>
              <StateCell colSpan={COLUMN_COUNT}>{emptyText}</StateCell>
            </tr>
          ) : (
            items.map((item) => (
              <Row key={item.ruleId} onClick={() => onRowClick(item)}>
                <Td>
                  <Badge $tone={statusTone(item.status)}>
                    {doseRuleStatusLabel(item.status)}
                  </Badge>
                </Td>
                <Td>
                  {item.hasGateIssue ? <Badge $tone="red">검산이슈</Badge> : <Muted>—</Muted>}
                </Td>
                <Td>
                  {item.isRepresentative ? (
                    <Badge $tone="blue">대표</Badge>
                  ) : (
                    <Badge $tone="gray">전개</Badge>
                  )}
                </Td>
                <Td>
                  {item.productName || <Muted>이름 없음</Muted>}
                  {hasVersionBadge(item.version) ? (
                    <>
                      {" "}
                      {/* 접근성 이름은 `룰 버전 vN`, `title`은 `룰 버전 N`(보정 7b).
                          보이는 글자는 `vN` 그대로다 — 목록 행 높이를 늘리면 안 된다. */}
                      <VersionBadge
                        $tone="gray"
                        data-testid="dose-rule-version-badge"
                        title={`${VERSION_SECTION_TITLE} ${item.version}`}
                      >
                        <SrOnly>{VERSION_SECTION_TITLE} </SrOnly>
                        {formatVersionNumber(item.version)}
                      </VersionBadge>
                    </>
                  ) : null}
                  <CodeLine>
                    {item.itemCode || "—"}
                    {item.standardCode ? ` · ${item.standardCode}` : ""}
                  </CodeLine>
                </Td>
                <Td>{item.type || <Muted>—</Muted>}</Td>
                <Td>{item.reviewedBy || <Muted>—</Muted>}</Td>
                <Td>{formatDateTime(item.updatedAt)}</Td>
              </Row>
            ))
          )}
        </tbody>
      </Table>
    </TableWrap>
  );
};

export default DoseRuleListSection;
